import type { ReactNode } from "react";

type TColor = "black" | "grey" | "warning" | "danger" | "success" | "info" | "disabled";

interface TypographyProps {
    /**
     * Text content inside the component tags
     */
    children: ReactNode;
    /**
     * Color of the text (black or gray, defaults to black)
     */
    color?: TColor;
    /**
     * If you need to add additional class names
     */
    className?: string;
    /**
     * Add additional styles
     */
    style?: React.CSSProperties;
    /**
     * If an onClick function is needed
     */
    onClick?: any;
    /**
     * If the text should be thin, will apply a fontWeight of 300
     */
    thin?: boolean;
    /**
     * If the text should be italic, will apply a fontStyle of italic
     */
    italic?: boolean;
    /**
     * html title attribute
     */
    title?: string;
}

type ConditionalProps =
    | {
          tag?: "label";
          htmlFor: string;
      }
    | {
          tag?: "div";
      }
    | {
          tag?: "span";
      };

type Props = TypographyProps & ConditionalProps;

/**
 * Typography components
 * Steps to create a new component
 * 1. Create a new exported function with the name of the component (examples right below Base function)
 * 2. Create a new style object in the styles function
 * Optionnally, you can add aliases by giving the new component the same name as an existing one
 */

const colors: { [key: string]: string } = {
    black: "#141516",
    grey: "var(--foreground)",
    disabled: "var(--foreground-disabled)",
    warning: "var(--warning)",
    danger: "var(--danger-text)",
    success: "var(--success)",
    info: "var(--info)",
};

const styles = (color: TColor = "black"): { [key: string]: React.CSSProperties } => ({
    pageTitle: {
        fontWeight: 600,
        fontSize: "24px",
        color: colors[color],
    },
    panelTitle: {
        fontWeight: 600,
        fontSize: "18px",
        color: colors[color],
    },
    body: {
        fontWeight: 400,
        fontSize: "14px",
        color: colors[color],
    },
    bodyHighlight: {
        fontWeight: 600,
        fontSize: "14px",
        color: colors[color],
    },
    decoTitle: {
        fontWeight: 600,
        fontSize: "36px",
        color: colors[color],
    },
    smallBodyHighlight: {
        fontWeight: 600,
        fontSize: "12px",
        color: colors[color],
    },
    smallBody: {
        fontWeight: 400,
        fontSize: "12px",
        color: colors[color],
    },
    tinyBody: {
        fontWeight: 400,
        fontSize: "10px",
        color: colors[color],
    },
    sectionTitle: {
        fontWeight: 400,
        fontSize: "18px",
        color: colors[color],
    },
});

const Base = (type: string) =>
    function BaseComponent({
        color,
        className,
        style,
        onClick,
        tag = "div",
        children,
        thin = false,
        italic = false,
        ...rest
    }: Props) {
        const Tag = `${tag}` as keyof JSX.IntrinsicElements;

        const conditionalStyles = {
            ...(onClick && { cursor: "pointer" }),
            ...(thin && { fontWeight: 300 }),
            ...(italic && { fontStyle: "italic" }),
        };

        return (
            <Tag
                {...rest}
                id={type}
                style={{
                    ...styles(color)[type],
                    ...style,
                    ...conditionalStyles,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
                className={className}
                onClick={onClick}
            >
                {children}
            </Tag>
        );
    };

// PageTitle
export const PageTitle = (props: Props) => Base("pageTitle")(props);
export const Heading2 = PageTitle;
export const H2 = PageTitle;

// PanelTitle
export const PanelTitle = (props: Props) => Base("panelTitle")(props);

// Body
export const Body = (props: Props) => Base("body")(props);

// BodyHighlight
export const BodyHighlight = (props: Props) => Base("bodyHighlight")({ ...props, color: props.color || "grey" });
export const Heading3 = BodyHighlight;
export const H3 = BodyHighlight;

// DecoTitle
export const DecoTitle = (props: Props) => Base("decoTitle")(props);
export const Heading1 = DecoTitle;
export const H1 = DecoTitle;

// SmallBodyHighlight
export const SmallBodyHighlight = (props: Props) => Base("smallBodyHighlight")(props);

// SmallBodyHighlight
export const SmallBody = (props: Props) => Base("smallBody")(props);

// TinyBody
export const TinyBody = (props: Props) => Base("tinyBody")(props);

// SectionTitle
export const SectionTitle = (props: Props) => Base("sectionTitle")(props);
